import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setShowReportsPanel } from "../../../../redux/action/Dashboard-action";
import {
  currValidationFieldName,
  graphicsLayer,
  view,
} from "../../../../utils/API";
import {
  filterReportTags,
  groupReportsByValidationField,
} from "../../../../utils/helper";
import { getLayerSymbology } from "../../../../utils/symbologies";
import { ReportTag } from "../../../SharedInformation/SharedInformationCard/SharedInformationCard-styled";
import { EditorContext } from "../EditorContextProvider";
import { EditType, pointSymbol } from "../EditorSwiper/EditorSwiper";
import AttributeFields from "./AttributeFields/AttributeFields";
import BatchFieldsHeader from "./BatchFieldsHeader";
import EditableFields from "./EditableFields/EditableFields";
import EditorSituationalFields from "./EditorSituationalFields";

export const filledInColor = "#16a34a";

const featureConformsDef = (feature, fields, value) => {
  const values = value.split(",");
  if (fields.length !== values.length) return false;

  return (
    values.filter((value, idx) => {
      if (value === "*") return true;

      const fieldName = fields[idx].name;
      const fieldValue = feature.attributes[fieldName];

      const range = value.split("-");
      if (range.length === 2) {
        //Range specified
        return (
          typeof fieldValue === "number" &&
          fieldValue >= range[0] &&
          fieldValue < range[1]
        );
      }
      //Single value specified
      else
        return typeof fieldValue === "number"
          ? fieldValue === Number(value)
          : fieldValue === value;
    }).length === values.length
  );
};

export const getFeatureIcon = (feature, symbology, layer = undefined) => {
  let iconName = symbology.defaultIcon;

  if (symbology?.iconMap) {
    const values = [];
    symbology.iconMap.fields.map((fieldName) => {
      const value = feature.attributes[fieldName];
      values.push(value || "*");
    });

    iconName = symbology?.iconMap.default[values.join(",")];
    if (!iconName) {
      const featureLayer = layer || feature.layer;
      if (
        featureLayer &&
        featureLayer.renderer &&
        featureLayer.renderer.getIconDefinitions
      ) {
        const iconDef = featureLayer.renderer
          .getIconDefinitions()
          .find((iconDef) =>
            featureConformsDef(feature, iconDef.field, iconDef.value)
          );
        return iconDef?.icon || symbology.defaultIcon;
      }

      return symbology.defaultIcon;
    }
  }

  return iconName;
};

//remove
export const getFeatureNameField = (editableLayer) => {
  const titleTemplate = editableLayer?.layerConfig?.titleTemplate;
  const name = titleTemplate
    ?.substring(1, titleTemplate.length - 1)
    ?.replace("feature.", "");
  if (name) {
    return name;
  }

  return null;
};

const EditorFields = ({
  editableLayer,
  config,
  editType,
  isRequired,
  updateType,
  initialUpdateType,
  handleSubmit,
  getIsDeletedField,
  doDelete,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [showBaseline, setShowBaseline] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const dragHandle = useRef();
  const { t } = useTranslation("common");

  const {
    highlightSymbol,
    highlightFeature,
    setHighlightFeature,
    setAddedPoints,
    popupFeature,
    batchUpdateFeatures,
    activeColor,
    setHighlightSymbol,
    situationalUpdateType,
    setSituationalUpdateType,
    requiredFields,
    setUpdateType,
    addedPoint,
    reportsFeatureMap,
  } = useContext(EditorContext);

  const { setValue } = useFormContext();
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      editType === EditType.create &&
      editableLayer?.layerConfig.titleLabel === "roadsSituational" &&
      graphicsLayer
    ) {
      const iso3 = new Set();
      graphicsLayer.graphics.forEach((graphic) => {
        if (graphic?.id === "route-combined" && graphic?.attributes?.iso3) {
          iso3.add(graphic.attributes.iso3);
        }
      });

      if (iso3.size > 1) {
        setShowBaseline(false);
      } else if (graphicsLayer.graphics.length > 0) {
        const graphic = graphicsLayer.graphics.toArray()[0];
        if (graphic) {
          Object.keys(graphic.attributes).forEach((key) => {
            const value = graphic.attributes[key];

            if (value) {
              setValue(key, value);
            }
          });
        }
      }
    }
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (initialUpdateType && updateType !== initialUpdateType) {
      setUpdateType(initialUpdateType);
    }
  }, []),
    useEffect(() => {
      let timer;
      return;
      if (!highlightFeature || editableLayer?.geometryType !== "point") return;
      dragHandle.current?.remove();
      dragHandle.current = view.on("drag", async (event) => {
        if (event.action === "start") {
          //Drag starting, search for graphics around the mouse
        } else if (event.action === "update" || event.action === "end") {
          //Dragging: adjust position
          highlightFeature.geometry = view.toMap({ x: event.x, y: event.y });
          const nGraphic = highlightFeature?.clone();
          nGraphic.symbol = highlightSymbol ? highlightSymbol : pointSymbol;

          graphicsLayer.removeAll();
          graphicsLayer.add(nGraphic);
          clearTimeout(timer);
          timer = setTimeout(() => {
            setAddedPoints([nGraphic.geometry]);
            setHighlightFeature(nGraphic);
          }, 250);

          event.stopPropagation();
        }
      });
      return () => {
        clearTimeout(timer);
        dragHandle.current?.remove();
      };
    }, [editableLayer, highlightSymbol, highlightFeature]);

  const handleChange = useCallback(
    ({ name, value }) => {
      if (highlightFeature) {
        const clonedFeature = highlightFeature.clone();
        clonedFeature.attributes[name] = value;

        setHighlightFeature(clonedFeature);
      }
    },
    [highlightFeature]
  );

  const groupedReportsTags = useMemo(() => {
    const id = highlightFeature?.getObjectId();

    if (!id) return [];
    const reports = reportsFeatureMap[id];

    const reportTags = groupReportsByValidationField(reports, config, t);
    return filterReportTags(reportTags);
  }, [config, t, highlightFeature, reportsFeatureMap]);

  const openReportsPanel = useCallback(() => {
    dispatch(setShowReportsPanel(true));
  }, []);

  const currValidationField = useMemo(() => {
    if (!editableLayer || !Array.isArray(editableLayer.fields)) return null;

    return editableLayer.fields.find((f) => f.name === currValidationFieldName);
  }, [editableLayer]);

  if (!editableLayer || !isActive) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        paddingTop: 6,
        boxSizing: "border-box",
      }}
    >
      {groupedReportsTags.length > 0 && (
        <div
          style={{
            padding: "0px 14px 8px 14px",
            marginTop: -6,
            display: "flex",
            gap: 4,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--fontColor)" }}>
            {t("screen.widget.Editor.manager.reports.updatesKpiTitle")}:
          </span>
          {groupedReportsTags?.map((tag) => {
            return tag.count > 0 ? (
              <ReportTag
                key={tag.title}
                onClick={openReportsPanel}
                bg={tag.color}
                color="#ffffff"
                textTransform="lowercase"
              >
                {tag.count} {tag.title}
              </ReportTag>
            ) : null;
          })}
        </div>
      )}

      {currValidationField && (
        <div
          style={{
            padding: "0px 14px 14px 14px",
          }}
        >
          <AttributeFields
            fields={[currValidationField]}
            config={config}
            t={t}
            symbology={getLayerSymbology(editableLayer, config)}
            onChangeHandler={handleChange}
            editableLayer={editableLayer}
            requiredFields={requiredFields}
          />
        </div>
      )}

      {!highlightFeature && batchUpdateFeatures.length > 0 && (
        <BatchFieldsHeader
          editableLayer={editableLayer}
          features={batchUpdateFeatures}
          config={config}
          activeColor={activeColor}
        />
      )}

      <div
        style={{
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {batchUpdateFeatures.length > 0 && !highlightFeature ? (
          <EditorSituationalFields
            editableLayer={editableLayer}
            requiredFields={requiredFields}
            highlightFeature={highlightFeature}
            activeColor={activeColor}
            batchUpdateFeatures={batchUpdateFeatures}
            situationalUpdateType={setSituationalUpdateType}
            setSituationalUpdateType={setSituationalUpdateType}
            updateType={updateType}
            editType={editType}
          />
        ) : (
          <EditableFields
            handleSubmit={handleSubmit}
            handleChange={handleChange}
            editType={editType}
            getIsDeletedField={getIsDeletedField}
            doDelete={doDelete}
            showBaseline={showBaseline}
          />
        )}
      </div>
    </div>
  );
};

export default EditorFields;
